/**
 * TargetedAccountsCard — top emails and businesses ordered by
 * **high-risk events received** in the active window. Replaces the
 * customer-investigation purpose of the removed Customers tab,
 * anchored on the Overview where investigators already start.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { MonoValue } from "@/components/primitives/MonoValue";
import { fmtNumber } from "@/lib/format";
import { truncateMiddle } from "@/lib/format";
import type { EntityTypeV1 } from "@/types/v1";

interface Props {
  onEntity?: (type: EntityTypeV1, value: string) => void;
}

export function TargetedAccountsCard({ onEntity }: Props) {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["targeted-accounts", filters, tick],
    queryFn: ({ signal }) => api.targetedAccounts(filters, signal),
  });

  const data = q.data?.data;

  return (
    <Card>
      <CardHeader
        title="Most-targeted accounts"
        subtitle="Customer accounts and businesses with the most high-risk events in the active window"
      />
      {q.isLoading ? (
        <Skeleton className="h-48" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !data || (data.emails.length === 0 && data.businesses.length === 0) ? (
        <EmptyState title="No high-risk traffic targeting tracked accounts in this window." />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          <Column title="Emails" rows={data.emails} type="email" onEntity={onEntity} />
          <Column title="Businesses" rows={data.businesses} type="business" onEntity={onEntity} />
        </div>
      )}
    </Card>
  );
}

function Column({
  title,
  rows,
  type,
  onEntity,
}: {
  title: string;
  rows: { value: string; highRisk: number; events: number }[];
  type: EntityTypeV1;
  onEntity?: (type: EntityTypeV1, value: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <div>
        <div className="mb-1.5 text-[11px] font-medium tracking-wide text-fg-subtle">{title}</div>
        <div className="rounded border border-border bg-surface-2 px-3 py-3 text-xs text-fg-subtle">No matches.</div>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-medium tracking-wide text-fg-subtle">{title}</div>
      <ul className="divide-y divide-border overflow-hidden rounded border border-border bg-surface-2">
        {rows.map((r) => (
          <li key={r.value} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
            <MonoValue
              value={truncateMiddle(r.value, 32)}
              onClick={onEntity ? () => onEntity(type, r.value) : undefined}
            />
            <span className="flex items-center gap-2 whitespace-nowrap">
              <span className="font-mono text-sev-high" title="High-risk events">
                {fmtNumber(r.highRisk, { compact: true })}
              </span>
              <span className="text-fg-subtle" title="Total events">/ {fmtNumber(r.events, { compact: true })}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
